import React, { useEffect, useState } from 'react';
import { Box, Text, useToast, VStack, Icon } from '@chakra-ui/react';
import { FiAlertCircle } from 'react-icons/fi';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import CampaignChatInterface from '../components/campaigns/CampaignChatInterface';
import { useCampaign } from '../hooks/useCampaigns';
import { useAuth } from '../contexts/AuthContext';

/** Keep in sync with `Header` main bar: `<Box as="header" … h="48px" />` */
const CAMPAIGN_PAGE_SHELL_H = 'calc(100vh - 48px)';

/** Match app empty-state surfaces: charcoal panel, subtle border, centered copy */
const CampaignProblemPanel = ({ title, description }) => (
  <Box
    minH={CAMPAIGN_PAGE_SHELL_H}
    h={CAMPAIGN_PAGE_SHELL_H}
    w="100%"
    bg="#0a0a0a"
    display="flex"
    alignItems="center"
    justifyContent="center"
    px={4}
  >
    <Box
      bg="#2d333b"
      borderRadius="md"
      border="1px solid rgba(255,255,255,0.12)"
      maxW="md"
      w="100%"
      py={{ base: 10, md: 12 }}
      px={{ base: 8, md: 10 }}
      textAlign="center"
    >
      <VStack spacing={5}>
        <Icon
          as={FiAlertCircle}
          boxSize={12}
          color="rgba(252,165,165,0.85)"
          strokeWidth={1}
        />
        <Text fontSize="lg" fontWeight="700" color="#fafafa" letterSpacing="-0.02em">
          {title}
        </Text>
        <Text fontSize="sm" color="#9ca3af" lineHeight={1.65}>
          {description}
        </Text>
      </VStack>
    </Box>
  </Box>
);

const CampaignPage = () => {
  const { id: campaignId } = useParams(); // Will be undefined for new campaigns
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { fetchUserProfile, isAnonymous } = useAuth();
  
  const [isValidId, setIsValidId] = useState(true);
  
  // Check if this is a new user onboarding flow or trial mode
  const isWelcome = searchParams.get('welcome') === 'true';
  const isTrial = searchParams.get('trial') === 'true';
  
  
  // Validate campaign exists (for error handling) - don't block on loading
  const { 
    isLoading, 
    error,
    isError 
  } = useCampaign(campaignId, campaignId && campaignId !== 'new' && isValidId);
  

  

  
  // Clean up welcome/trial query parameter after delay (let child component use it first)
  // Only clean up if we're still on the /new route - don't interfere with actual campaign routes
  useEffect(() => {
    if ((isWelcome || isTrial) && campaignId === 'new') {
      // Wait 2 seconds before removing the param to ensure child component has processed it
      const timer = setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        params.delete('welcome');
        params.delete('trial');
        const newSearch = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isWelcome, isTrial, campaignId]);

  // Store anonymous campaign in sessionStorage to persist across reloads
  useEffect(() => {
    if (isAnonymous && campaignId && campaignId !== 'new') {
      sessionStorage.setItem('anonymous_campaign_id', campaignId);
    }
  }, [isAnonymous, campaignId]);

  // Navigation guard for anonymous users - only warn on actual navigation, not reload
  // Note: We don't use beforeunload because it triggers on refresh too
  // Instead, campaigns persist in sessionStorage and expire after 24 hours via backend cleanup

  // Handle Stripe checkout success and execute pending actions
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('stripe_success') === 'true';
    const stripeCancel = urlParams.get('stripe_cancel') === 'true';
    const paymentSuccess = urlParams.get('payment_success') === 'true';
    
    if (stripeSuccess || paymentSuccess) {
      // Clean URL
      urlParams.delete('stripe_success');
      urlParams.delete('payment_success');
      const newSearch = urlParams.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (newSearch ? `?${newSearch}` : ''));
      
      // Refetch user profile to get updated credits
      fetchUserProfile().then(() => {
        toast({
          title: 'Payment Successful!',
          description: 'Your credits have been added to your account.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      });
      
      // Dispatch event to refresh credit count in header
      window.dispatchEvent(new Event('exportCompleted'));
    } else if (stripeCancel || urlParams.get('payment_canceled') === 'true') {
      // Clean URL
      urlParams.delete('stripe_cancel');
      urlParams.delete('payment_canceled');
      const newSearch = urlParams.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (newSearch ? `?${newSearch}` : ''));
      
      toast({
        title: 'Payment Cancelled',
        description: 'You can purchase credits anytime.',
        status: 'info',
        duration: 3000
      });
    }
  }, [campaignId, fetchUserProfile, toast]);

  // Validate campaign ID if provided
  useEffect(() => {
    if (campaignId && campaignId !== 'new' && campaignId.length < 5) {
      setIsValidId(false);
      toast({
        title: 'Invalid campaign ID',
        description: 'The campaign ID provided is not valid.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/');
    } else if (campaignId) {
      setIsValidId(true);
    }
  }, [campaignId, navigate, toast]);
  
  // Show error toast when campaign fetch fails
  useEffect(() => {
    if (isError && error) {
      toast({
        title: 'Error loading campaign',
        description: error.message || 'Failed to load campaign data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [isError, error, toast]);

  // Only block for critical errors (not loading - let CampaignChatInterface handle its own loading)
  if (campaignId && campaignId !== 'new' && !isValidId) {
    return (
      <CampaignProblemPanel
        title="Invalid Campaign"
        description={error?.message || 'The campaign ID is invalid.'}
      />
    );
  }

  // Show error only after loading completes and there's a definite error
  if (campaignId && campaignId !== 'new' && !isLoading && isError) {
    return (
      <CampaignProblemPanel
        title="Error loading campaign"
        description={error?.message || 'Failed to load campaign data'}
      />
    );
  }

  return (
    <Box minH={CAMPAIGN_PAGE_SHELL_H} h={CAMPAIGN_PAGE_SHELL_H} w="100%" bg="#0a0a0a" overflow="hidden">
      <CampaignChatInterface 
        campaignId={campaignId === 'new' ? undefined : campaignId}
        autoSendWelcomeMessage={isWelcome || isTrial}
      />
    </Box>
  );
};

export default CampaignPage; 